import { atom } from 'nanostores';
import type { AppBundle } from '@/utils/app-bundles';
import { getAppBundleFromUrl } from '@/utils/navigation';

export interface LocalCommand {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  keywords?: string[];
  onSelect: () => void | Promise<void>;
}

export interface ProviderEntry {
  commands: LocalCommand[];
  app: AppBundle | null;
  providerId: string;
}

let nextRegistrationId = 0;

export const localCommandsAtom = atom<Map<string, ProviderEntry>>(new Map());

function unregister(key: string): void {
  const current = new Map(localCommandsAtom.get());
  current.delete(key);
  localCommandsAtom.set(current);
}

export function register(providerId: string, commands: LocalCommand[]): () => void {
  const id = nextRegistrationId++;
  const key = `${id}-${providerId}`;
  const app = getAppBundleFromUrl(window.location.pathname);
  const entry: ProviderEntry = { commands, app, providerId };

  const current = new Map(localCommandsAtom.get());
  // Replace only existing registration for this providerId in this app, so that
  // re-registering (e.g. updating commands) doesn't duplicate, but a different
  // app using the same providerId does not remove the other app's commands.
  const keysToDelete = [...current.entries()]
    .filter(([, entry]) => entry.providerId === providerId && entry.app === app)
    .map(([k]) => k);

  for (const k of keysToDelete) {
    current.delete(k);
  }

  current.set(key, entry);
  localCommandsAtom.set(current);

  return () => unregister(key);
}

// Expose to window (follows sidebar pattern)
Object.assign(window, {
  __hydra_commands__: {
    register,
  } satisfies Window['__hydra_commands__'],
});
